<template>
  <div class="eco-portal">
    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 英雄区域 -->
      <section class="hero-section">
        <div class="hero-content">
          <h1>{{ $t('portal.hero.title') }}</h1>
          <p>{{ $t('portal.hero.subtitle') }}</p>
          <div class="lang-box">
            <LanguageSwitch />
          </div>
        </div>
      </section>

      <!-- 功能模块 -->
      <div class="container">
        <h2 class="section-title">
          {{ $t('portal.features.title') }}
        </h2>
        <div class="features-grid">
          <van-card
            v-for="feature in features"
            :key="feature.id"
            class="feature-card"
            :class="`feature-${feature.id}`"
          >
            <template #title>
              <div class="feature-icon">
                <van-icon :name="feature.icon" />
              </div>
            </template>
            <template #footer>
              <div class="feature-content">
                <h3>{{ $t(`portal.features.${feature.key}.title`) }}</h3>
                <p>{{ $t(`portal.features.${feature.key}.description`) }}</p>
                <a
                  href="#"
                  class="feature-link"
                  @click.prevent="handleFeatureClick(feature)"
                >
                  {{ $t('portal.features.experienceLink') }} <van-icon name="arrow" />
                </a>
              </div>
            </template>
          </van-card>
        </div>

        <!-- 品牌墙展示 -->
        <div class="brands-section">
          <h2 class="section-title">
            {{ $t('portal.brands.title') }}
          </h2>
          <div class="brand-wall">
            <div
              v-for="(brand, index) in brands"
              :key="brand.id"
              class="brand-item"
              :class="getBrandItemClass(index)"
              :style="getBrandItemStyle(brand, index)"
            >
              <div class="brand-content">
                <img
                  :src="brand?.logo"
                  :alt="brand.name"
                  class="brand-logo"
                  @load="handleImageLoad"
                >
                <div class="brand-overlay">
                  <span class="brand-name">{{ brand.name }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 页脚 -->
    <footer>
      <div class="container">
        <div class="footer-content">
          <div class="footer-column">
            <h3>{{ $t('portal.footer.about.title') }}</h3>
            <ul>
              <li
                v-for="item in footerLinks.about"
                :key="item.key"
              >
                <a
                  href="#"
                  @click.prevent="handleFeatureClick(item)"
                >{{ $t(`portal.footer.about.${item.key}`) }}</a>
              </li>
            </ul>
          </div>
          <div class="footer-column">
            <h3>{{ $t('portal.footer.cooperation.title') }}</h3>
            <ul>
              <li
                v-for="item in footerLinks.cooperation"
                :key="item.key"
              >
                <a
                  href="#"
                  @click.prevent="handleFeatureClick(item)"
                >{{ $t(`portal.footer.cooperation.${item.key}`) }}</a>
              </li>
            </ul>
          </div>
          <div class="footer-column">
            <h3>{{ $t('portal.footer.help.title') }}</h3>
            <ul>
              <li
                v-for="item in footerLinks.help"
                :key="item.key"
              >
                <a
                  href="#"
                  @click.prevent="handleFeatureClick(item)"
                >{{ $t(`portal.footer.help.${item.key}`) }}</a>
              </li>
            </ul>
          </div>
          <!--          <div class="footer-column">-->
          <!--            <h3>{{ $t('portal.footer.follow.title') }}</h3>-->
          <!--            <p>{{ $t('portal.footer.follow.description') }}</p>-->
          <!--            <div class="social-links">-->
          <!--              <a-->
          <!--                v-for="social in socialLinks"-->
          <!--                :key="social.key"-->
          <!--                href="#"-->
          <!--                class="social-link"-->
          <!--              >-->
          <!--                <van-icon :name="social.icon" />-->
          <!--              </a>-->
          <!--            </div>-->
          <!--          </div>-->
        </div>
        <div class="copyright">
          <p>{{ $t('portal.footer.copyright') }}</p>
        </div>
      </div>
    </footer>

    <!-- 联系邮箱弹窗 -->
    <van-dialog
      v-model:show="showContactDialog"
      :title="$t('portal.contact.title')"
      show-cancel-button
      :confirm-button-text="$t('common.copy')"
      :cancel-button-text="$t('common.close')"
      @confirm="copyContactEmail"
    >
      <div class="contact-dialog-content">
        <van-icon
          name="envelop-o"
          class="contact-icon"
        />
        <p class="contact-description">
          {{ $t('portal.contact.description') }}
        </p>
        <p class="contact-email">
          {{ contactEmail }}
        </p>
      </div>
    </van-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { showDialog, showSuccessToast, showToast } from 'vant'
import { queryBrandList } from '@/api/brand'
import LanguageSwitch from '@/components/layout/LanguageSwitch.vue'

export default {
  name: 'EcoPortal',
  components: {
    LanguageSwitch
  },
  setup () {
    const router = useRouter()
    const { t } = useI18n()

    // 功能模块数据
    const features = reactive([
      {
        id: 1,
        key: 'antiCounter',
        icon: 'shield-o',
        path: '/authGuard'
      },
      {
        id: 2,
        key: 'cooperation',
        icon: 'friends',
        path: '/cooperationCenter'
      },
      {
        id: 3,
        key: 'brandEcosystem',
        icon: 'cluster',
        path: '/brand'
      },
      {
        id: 4,
        key: 'calendar',
        icon: 'calendar-o',
        path: '/calendar'
      }
    ])

    // 品牌数据
    const brands = ref([])

    // 预定义品牌墙布局配置
    const brandWallConfig = computed(() => {
      const isMobile = window.innerWidth <= 768
      const isVertical = window.innerWidth <= 1080 && window.innerHeight >= 1920

      if (isVertical) {
        // 1080x1920 竖屏设备
        return {
          columns: 3,
          itemBaseWidth: 120,
          itemBaseHeight: 120,
          gap: 15
        }
      } else if (isMobile) {
        // 移动端
        return {
          columns: 4,
          itemBaseWidth: 80,
          itemBaseHeight: 80,
          gap: 10
        }
      } else {
        // PC端
        return {
          columns: 6,
          itemBaseWidth: 100,
          itemBaseHeight: 100,
          gap: 20
        }
      }
    })

    // 品牌尺寸变化范围
    const sizeVariations = [
      { width: 1, height: 1 }, // 正常尺寸
      { width: 1.2, height: 1.2 }, // 稍大
      { width: 0.8, height: 0.8 }, // 稍小
      { width: 1.5, height: 1 }, // 宽型
      { width: 1, height: 1.5 } // 高型
    ]

    // 旋转角度变化
    const rotationVariations = [-5, -3, 0, 3, 5]

    function getBrandList () {
      const params = {
        page: 1,
        size: 99 // 获取更多品牌用于品牌墙展示
      }
      queryBrandList(params).then(res => {
        brands.value = res.list
      })
    }

    // 获取品牌项样式
    const getBrandItemStyle = (brand, index) => {
      const config = brandWallConfig.value
      const sizeVar = sizeVariations[index % sizeVariations.length]
      const rotation = rotationVariations[index % rotationVariations.length]

      const width = config.itemBaseWidth * sizeVar.width
      const height = config.itemBaseHeight * sizeVar.height

      return {
        width: `${width}px`,
        height: `${height}px`,
        transform: `rotate(${rotation}deg)`
      }
    }

    // 获取品牌项类名
    const getBrandItemClass = (index) => {
      const classes = []
      // 添加层级类，创造堆叠效果
      if (index % 7 === 0) classes.push('brand-layer-1')
      else if (index % 5 === 0) classes.push('brand-layer-2')
      else if (index % 3 === 0) classes.push('brand-layer-3')
      else classes.push('brand-layer-4')

      return classes.join(' ')
    }

    // 图片加载完成处理
    const handleImageLoad = (event) => {
      // 可以添加图片加载后的动画效果
      event.target.parentElement.classList.add('loaded')
    }

    // 统计数据
    const stats = reactive([
      {
        key: 'brands',
        value: '1,258'
      },
      {
        key: 'departmentStores',
        value: '326'
      },
      {
        key: 'designers',
        value: '5,842'
      },
      {
        key: 'officers',
        value: '128'
      }
    ])

    // 页脚链接
    const footerLinks = reactive({
      about: [
        { key: 'introduction', url: 'http://www.geekfashiongroup.com' },
        // { key: 'introduction', path: '/groupIntroduction' },
        // { key: 'history', path: '/cooperation/departmentStore' },
        { key: 'contact' }
      ],
      cooperation: [
        { key: 'departmentStores', path: '/cooperation/departmentStore' },
        { key: 'brands', path: '/cooperation/brandApplication' },
        { key: 'designers', path: '/cooperation/designerApplication' },
        { key: 'officers', path: '/cooperation/officerApplication' }
      ],
      help: [
        { key: 'faq', path: '/faq' }
        // { key: 'guide', path: '/cooperation/departmentStore' },
        // { key: 'terms', path: '/cooperation/departmentStore' },
        // { key: 'privacy', path: '/cooperation/departmentStore' }
      ]
    })

    // 社交媒体链接
    const socialLinks = reactive([
      {
        key: 'wechat',
        icon: 'wechat'
      },
      {
        key: 'weibo',
        icon: 'weibo'
      },
      {
        key: 'douyin',
        icon: 'photo'
      },
      {
        key: 'xiaohongshu',
        icon: 'notes'
      }
    ])

    // 方法
    const handleExperience = () => {
      showToast(t('portal.hero.experienceToast'))
    }

    const handleLearnMore = () => {
      router.push('/about')
    }

    const handleFeatureClick = (feature) => {
      if (feature.key === 'contact') {
        // 显示联系邮箱弹窗
        showContactDialog.value = true
      }
      if (feature.key === 'introduction') {
        showDialog({
          title: t('common.systemMessage'),
          message: t('portal.hero.introductionContent'),
          confirmButtonText: t('common.ok')
        })
        return
      }
      if (feature?.path) {
        router.push({
          path: feature.path
        })
      } else if (feature?.url) {
        window.open(feature.url, '_blank')
      }
    }

    const showContactDialog = ref(false)
    const contactEmail = 'buttonpie@zhuritec.com'
    // 复制联系邮箱
    const copyContactEmail = async () => {
      try {
        await navigator.clipboard.writeText(contactEmail)
        showSuccessToast(t('portal.contact.copySuccess'))
      } catch (err) {
        // 如果clipboard API不可用，使用备用方案
        const textArea = document.createElement('textarea')
        textArea.value = contactEmail
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        showSuccessToast(t('portal.contact.copySuccess'))
      }
    }

    onMounted(() => {
      getBrandList()
    })

    return {
      features,
      brands,
      stats,
      footerLinks,
      socialLinks,
      handleExperience,
      handleLearnMore,
      handleFeatureClick,
      getBrandItemStyle,
      getBrandItemClass,
      handleImageLoad,
      showContactDialog,
      contactEmail,
      copyContactEmail
    }
  }
}
</script>

<style lang="scss" scoped>
.eco-portal {
  min-height: 100vh;
  background-color: #f8fafc;
  color: #334155;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* 英雄区域样式 */
.hero-section {
  background: linear-gradient(135deg, #1e3a8a, #3b82f6);
  color: white;
  padding: 60px 20px;
  text-align: center;
  margin-bottom: 40px;
}

.hero-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  .lang-box{
    position: fixed;
    right: 20px;
    top: 20px;
  }

  h1 {
    font-size: 42px;
    margin-bottom: 16px;
    font-weight: 700;

    @media (max-width: 768px) {
      font-size: 30px;
    }
  }

  p {
    font-size: 18px;
    margin: 0 auto 30px;
    opacity: 0.9;
    max-width: 800px;
    line-height: 1.8;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
}

/* 区域标题样式 */
.section-title {
  font-size: 28px;
  margin-bottom: 30px;
  color: #1e293b;
  position: relative;
  padding-bottom: 12px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 60px;
    height: 4px;
    background: #3b82f6;
    border-radius: 2px;
  }
}

/* 功能模块卡片样式 */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-bottom: 60px;
}

.feature-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s, box-shadow 0.3s;
  border: 1px solid #f1f5f9;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 25px rgba(0, 0, 0, 0.1);
  }

  :deep(.van-card__header) {
    padding: 0;
  }

  :deep(.van-card__footer) {
    padding: 24px;
  }
}

.feature-icon {
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: white;

  .van-icon {
    font-size: 48px;
  }
}

.feature-1 .feature-icon {
  background: linear-gradient(135deg, #10b981, #34d399);
}

.feature-2 .feature-icon {
  background: linear-gradient(135deg, #f59e0b, #fbbf24);
}

.feature-3 .feature-icon {
  background: linear-gradient(135deg, #8b5cf6, #a78bfa);
}

.feature-4 .feature-icon {
  background: linear-gradient(135deg, #ef4444, #f87171);
}

.feature-content {
  h3 {
    font-size: 20px;
    margin-bottom: 12px;
    color: #1e293b;
  }

  p {
    color: #64748b;
    margin-bottom: 20px;
    line-height: 1.6;
  }
}

.feature-link {
  color: #3b82f6;
  text-decoration: none;
  font-weight: 500;
  display: flex;
  align-items: center;

  .van-icon {
    margin-left: 6px;
    transition: transform 0.3s;
  }

  &:hover .van-icon {
    transform: translateX(4px);
  }
}

/* 品牌墙样式 */
.brands-section {
  margin-bottom: 60px;
}

.brand-wall {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  padding: 30px;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 20px;
  min-height: 500px;
  align-content: flex-start;
  position: relative;

  // 添加墙面纹理
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
        radial-gradient(circle at 25% 25%, rgba(255,255,255,0.3) 2px, transparent 2px),
        radial-gradient(circle at 75% 75%, rgba(255,255,255,0.2) 1px, transparent 1px);
    background-size: 50px 50px, 30px 30px;
    pointer-events: none;
  }
}

.brand-item {
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border-radius: 12px;
  box-shadow:
      0 4px 15px rgba(0, 0, 0, 0.1),
      0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;

  // 不同层级的堆叠效果
  &.brand-layer-1 {
    z-index: 4;
    box-shadow:
        0 8px 25px rgba(0, 0, 0, 0.15),
        0 2px 6px rgba(0, 0, 0, 0.1);
  }

  &.brand-layer-2 {
    z-index: 3;
    box-shadow:
        0 6px 20px rgba(0, 0, 0, 0.12),
        0 2px 5px rgba(0, 0, 0, 0.08);
  }

  &.brand-layer-3 {
    z-index: 2;
  }

  &.brand-layer-4 {
    z-index: 1;
    opacity: 0.9;
  }

  &:hover {
    transform: scale(1.05) rotate(0deg) !important;
    box-shadow:
        0 12px 35px rgba(0, 0, 0, 0.2),
        0 4px 15px rgba(0, 0, 0, 0.1);
    z-index: 10;
  }
}

.brand-content {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px;

  .brand-logo {
    max-width: 80%;
    max-height: 80%;
    object-fit: contain;
    transition: all 0.3s ease;
    filter: grayscale(0.2);
  }

  .brand-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
    color: white;
    padding: 8px 12px;
    transform: translateY(100%);
    transition: transform 0.3s ease;
    text-align: center;

    .brand-name {
      font-size: 11px;
      font-weight: 600;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &:hover {
    .brand-logo {
      transform: scale(1.1);
      filter: grayscale(0);
    }

    .brand-overlay {
      transform: translateY(0);
    }
  }
}

/* 响应式调整 */
@media (max-width: 1080px) and (min-height: 1920px) {
  /* 1080x1920 竖屏设备 */
  .container {
    padding: 0 15px;
  }

  .hero-section {
    padding: 40px 15px;
    margin-bottom: 30px;
  }

  .hero-content h1 {
    font-size: 36px;
  }

  .section-title {
    font-size: 24px;
  }

  .brand-wall {
    gap: 15px;
    padding: 20px;
    min-height: 400px;
  }

  .brand-item {
    width: 120px !important;
    height: 120px !important;
  }
}

@media (max-width: 768px) {
  /* 移动端 */
  .container {
    padding: 0 16px;
  }

  .hero-section {
    padding: 40px 16px;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .brand-wall {
    gap: 10px;
    padding: 15px;
    min-height: 300px;
  }

  .brand-item {
    width: 80px !important;
    height: 80px !important;
  }

  .brand-content {
    padding: 8px;

    .brand-overlay .brand-name {
      font-size: 9px;
    }
  }

  .contact-dialog-content {
    padding: 16px;

    .contact-email {
      font-size: 16px;
    }
  }
}

@media (min-width: 1200px) {
  /* PC端大屏幕 */
  .brand-wall {
    gap: 25px;
    padding: 40px;
    min-height: 600px;
  }
}

/* 页脚样式 */
footer {
  background: #1e293b;
  color: #cbd5e1;
  padding: 50px 0 20px;
}

.footer-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 40px;
  margin-bottom: 40px;
}

.footer-column {
  h3 {
    font-size: 18px;
    margin-bottom: 20px;
    color: white;
    position: relative;
    padding-bottom: 10px;

    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: calc(50% - 20px);
      width: 40px;
      height: 2px;
      background: #3b82f6;
    }
  }

  ul {
    list-style: none;
    padding: 0;
  }

  li {
    margin-bottom: 12px;
  }

  a {
    color: #94a3b8;
    text-decoration: none;
    transition: color 0.3s;

    &:hover {
      color: white;
    }
  }

  p {
    color: #94a3b8;
    margin-bottom: 20px;
  }
}

.social-links {
  display: flex;
  gap: 16px;

  .social-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    color: #94a3b8;
    transition: all 0.3s;

    &:hover {
      background: #3b82f6;
      color: white;
      transform: translateY(-3px);
    }

    .van-icon {
      font-size: 18px;
    }
  }
}

.copyright {
  text-align: center;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  color: #94a3b8;
  font-size: 14px;
}

/* 联系邮箱弹窗样式 */
.contact-dialog-content {
  padding: 20px;
  text-align: center;

  .contact-icon {
    font-size: 48px;
    color: #3b82f6;
    margin-bottom: 16px;
  }

  .contact-email {
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
    margin: 16px 0;
    padding: 12px;
    background: #f8fafc;
    border-radius: 8px;
    word-break: break-all;
  }

  .contact-description {
    color: #64748b;
    font-size: 14px;
    line-height: 1.5;
  }
}
</style>
